import React, { useState } from 'react';
import { Plus, Building2, Trash2, Edit3, X, ArrowDownCircle, ArrowUpCircle, Wallet } from 'lucide-react';
import { brokersAPI, cashMovementsAPI } from '../api/client';
import { useInvestmentContext } from '../context/InvestmentContext';
import { formatCurrency } from '../utils/formatters';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'COP', 'ARS', 'MXN', 'BRL'];

// ── Broker Form Modal ─────────────────────────────────────────────────────────

const BrokerForm = ({ broker, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    name: broker?.name || '',
    currency: broker?.currency || 'USD',
    account_number: broker?.account_number || '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErrors({ name: 'Name is required.' }); return; }
    setLoading(true);
    setErrors({});
    try {
      await onSubmit({
        name: form.name.trim(),
        currency: form.currency,
        account_number: form.account_number.trim() || null,
      });
    } catch (err) {
      setErrors(err.response?.data || {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">{broker ? 'Edit Broker' : 'New Broker'}</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Broker Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({ ...errors, name: undefined }); }}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.name ? 'border-red-400' : 'border-gray-200'}`}
              placeholder="e.g. Interactive Brokers"
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Number (optional)</label>
            <input
              type="text"
              value={form.account_number}
              onChange={(e) => setForm({ ...form, account_number: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="e.g. U1234567"
            />
          </div>
          {errors.non_field_errors && <p className="text-xs text-red-500">{errors.non_field_errors}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
              {loading ? 'Saving...' : broker ? 'Save Changes' : 'Create Broker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Cash Movement Modal ───────────────────────────────────────────────────────

const CashMovementModal = ({ broker, onClose, onSave }) => {
  const [form, setForm] = useState({
    movement_type: 'deposit',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0) {
      setError('Ingresa un monto válido mayor a 0.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSave({
        broker: broker.id,
        movement_type: form.movement_type,
        amount: amount,
        date: form.date,
        notes: form.notes.trim() || null,
      });
      onClose();
    } catch {
      setError('No se pudo registrar el movimiento.');
    } finally {
      setLoading(false);
    }
  };

  const isDeposit = form.movement_type === 'deposit';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Movimiento de efectivo</h2>
            <p className="text-sm text-gray-500">{broker.name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, movement_type: 'deposit' }))}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition ${
                isDeposit ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ArrowDownCircle className="w-4 h-4" /> Deposit
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, movement_type: 'withdrawal' }))}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition ${
                !isDeposit ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ArrowUpCircle className="w-4 h-4" /> Withdrawal
            </button>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ({broker.currency})</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => { setForm((f) => ({ ...f, amount: e.target.value })); setError(null); }}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="0.00"
              min="0.01"
              step="any"
              autoFocus
            />
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="e.g. Transferencia bancaria"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition ${isDeposit ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}
            >
              {loading ? 'Saving...' : isDeposit ? 'Register deposit' : 'Register withdrawal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Brokers Screen ────────────────────────────────────────────────────────────

const BrokersScreen = () => {
  const { brokers, brokersLoading, createBroker, fetchBrokers } = useInvestmentContext();
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBroker, setEditingBroker] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cashBroker, setCashBroker] = useState(null);

  const handleCreate = async (data) => {
    await createBroker(data);
    setShowForm(false);
  };

  const handleUpdate = async (data) => {
    try {
      await brokersAPI.update(editingBroker.id, data);
      await fetchBrokers();
      setEditingBroker(null);
      setShowForm(false);
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async (id) => {
    try {
      await brokersAPI.delete(id);
      await fetchBrokers();
    } catch {
      setError('Failed to delete broker.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleCashMovement = async (data) => {
    await cashMovementsAPI.create(data);
    await fetchBrokers();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Brokers</h1>
            <p className="text-gray-500 text-sm">{brokers.length} broker{brokers.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => { setEditingBroker(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New Broker
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-medium">Dismiss</button>
          </div>
        )}

        {brokersLoading && brokers.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : brokers.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No brokers yet</p>
            <p className="text-gray-400 text-sm mt-1">Add your first broker to start tracking transactions</p>
            <button
              onClick={() => { setEditingBroker(null); setShowForm(true); }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
            >
              Add Broker
            </button>
          </div>
        ) : (
          brokers.map((broker) => {
            const balance = parseFloat(broker.cash_balance || 0);
            const balancePositive = balance >= 0;
            return (
              <div key={broker.id} className="bg-white rounded-2xl shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{broker.name}</p>
                      <p className="text-sm text-gray-500">{broker.currency}{broker.account_number ? ` · ${broker.account_number}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCashBroker(broker)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition"
                      title="Registrar movimiento de efectivo"
                    >
                      <Wallet className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setEditingBroker(broker); setShowForm(true); }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(broker.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Cash balance */}
                <div className={`rounded-xl p-3 flex items-center justify-between ${balancePositive ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div>
                    <p className={`text-xs font-medium ${balancePositive ? 'text-green-600' : 'text-red-500'}`}>
                      Available balance
                    </p>
                    <p className={`text-xs mt-0.5 ${balancePositive ? 'text-green-500' : 'text-red-400'}`}>
                      deposits − withdrawals + sales + dividends − buys
                    </p>
                  </div>
                  <p className={`text-lg font-bold ${balancePositive ? 'text-green-700' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(balance), broker.currency)}
                    {!balancePositive && <span className="text-xs ml-1">(negativo)</span>}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showForm && (
        <BrokerForm
          broker={editingBroker}
          onSubmit={editingBroker ? handleUpdate : handleCreate}
          onClose={() => { setShowForm(false); setEditingBroker(null); }}
        />
      )}

      {cashBroker && (
        <CashMovementModal
          broker={cashBroker}
          onClose={() => setCashBroker(null)}
          onSave={handleCashMovement}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Broker?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will permanently delete the broker. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokersScreen;
